import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const WellnessProfileInformation = ({ user, onDataChange }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    wellnessGoals: user?.wellnessGoals || [],
    healthConsiderations: user?.healthConsiderations || '',
    emergencyContact: user?.emergencyContact || '',
    dateOfBirth: user?.dateOfBirth || '',
    practitionerCertifications: user?.practitionerCertifications || []
  });
  const [avatar, setAvatar] = useState(user?.avatar || '/assets/images/wellness_profile_default.png');
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const fileInputRef = useRef(null);

  const wellnessGoalOptions = [
    { id: 'stress-reduction', label: 'Stress Reduction', icon: 'Heart' },
    { id: 'energy-enhancement', label: 'Energy Enhancement', icon: 'Zap' },
    { id: 'spiritual-growth', label: 'Spiritual Growth', icon: 'Sparkles' },
    { id: 'pain-management', label: 'Pain Management', icon: 'Shield' },
    { id: 'mental-clarity', label: 'Mental Clarity', icon: 'Brain' },
    { id: 'emotional-balance', label: 'Emotional Balance', icon: 'Scale' },
    { id: 'sleep-improvement', label: 'Sleep Improvement', icon: 'Moon' },
    { id: 'detoxification', label: 'Detoxification', icon: 'Droplets' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    onDataChange?.();
  };

  const handleWellnessGoalToggle = (goalId) => {
    const updatedGoals = formData.wellnessGoals.includes(goalId)
      ? formData.wellnessGoals.filter(id => id !== goalId)
      : [...formData.wellnessGoals, goalId];
    
    handleInputChange('wellnessGoals', updatedGoals);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setTempImage(e.target?.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropSave = () => {
    if (tempImage) {
      setAvatar(tempImage);
      onDataChange?.();
    }
    setShowCropModal(false);
    setTempImage(null);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempImage(null);
  };

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
      {/* Header */}
      <div className="px-6 py-4 border-b border-blue-100">
        <h2 className="text-xl font-semibold text-slate-800 font-heading">
          Wellness Profile Information
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Share your wellness journey and health considerations for personalized experiences
        </p>
      </div>

      <div className="p-6">
        {/* Profile Photo Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 mb-8">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-blue-100 cursor-pointer border-4 border-white shadow-lg" onClick={handleAvatarClick}>
              <img
                src={avatar}
                alt="Wellness Profile"
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                onError={(e) => {
                  e.target.src = '/assets/images/wellness_profile_default.png';
                }}
              />
            </div>
            <button
              onClick={handleAvatarClick}
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Icon name="Camera" size={20} className="text-white" />
            </button>
            {user?.isVerified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                <Icon name="Check" size={12} className="text-white" />
              </div>
            )}
          </div>
          
          <div className="mt-4 sm:mt-0 flex-1">
            <h3 className="text-lg font-medium text-slate-800">{formData.name}</h3>
            <p className="text-slate-600 text-sm mb-1">
              Wellness community member since {new Date(user?.joinedDate).toLocaleDateString()}
            </p>
            <p className="text-slate-500 text-sm mb-3">
              Click on the photo to upload a new wellness profile picture
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleAvatarClick}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Upload Photo
              </button>
              <button
                onClick={() => {
                  setAvatar('/assets/images/wellness_profile_default.png');
                  onDataChange?.();
                }}
                className="border border-blue-200 text-slate-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
              <Icon name="User" size={18} className="mr-2 text-blue-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-slate-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500"
                  placeholder="City, State"
                />
              </div>

              <div>
                <label htmlFor="emergencyContact" className="block text-sm font-medium text-slate-700 mb-2">
                  Emergency Contact
                </label>
                <input
                  type="text"
                  id="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500"
                  placeholder="Name and phone number"
                />
              </div>
            </div>
          </div>

          {/* Wellness Goals */}
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
              <Icon name="Target" size={18} className="mr-2 text-blue-600" />
              Wellness Goals
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Select your primary wellness objectives to receive personalized EES center recommendations
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {wellnessGoalOptions.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => handleWellnessGoalToggle(goal.id)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                    formData.wellnessGoals.includes(goal.id)
                      ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-blue-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name={goal.icon} size={18} className={formData.wellnessGoals.includes(goal.id) ? 'text-blue-600' : 'text-slate-500'} />
                    <span className="text-xs text-center">{goal.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Health Considerations */}
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
              <Icon name="Shield" size={18} className="mr-2 text-blue-600" />
              Health Considerations
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-2">
                <Icon name="Lock" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  Your health information is encrypted and only shared with practitioners upon your explicit consent
                </p>
              </div>
            </div>
            <textarea
              id="healthConsiderations"
              rows={4}
              value={formData.healthConsiderations}
              onChange={(e) => handleInputChange('healthConsiderations', e.target.value)}
              className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500 resize-none"
              placeholder="Please share any health conditions, medications, or considerations that EES practitioners should be aware of..."
              maxLength={1000}
            />
            <p className="text-xs text-slate-500 mt-1">
              {formData.healthConsiderations.length}/1000 characters
            </p>
          </div>

          {/* Wellness Bio */}
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
              <Icon name="MessageSquare" size={18} className="mr-2 text-blue-600" />
              Wellness Journey Bio
            </h3>
            <textarea
              id="bio"
              rows={4}
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className="block w-full px-4 py-3 border border-blue-200 rounded-md shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 bg-white text-slate-800 placeholder-slate-500 resize-none"
              placeholder="Share your wellness journey, what brought you to EES therapy, and what you hope to achieve..."
              maxLength={500}
            />
            <p className="text-xs text-slate-500 mt-1">
              {formData.bio.length}/500 characters
            </p>
          </div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Crop Wellness Profile Photo</h3>
            
            <div className="mb-6">
              <div className="w-full h-64 bg-blue-50 rounded-lg overflow-hidden flex items-center justify-center">
                {tempImage && (
                  <img
                    src={tempImage}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Crop functionality would be implemented here with a proper image cropping library
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleCropSave}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Save
              </button>
              <button
                onClick={handleCropCancel}
                className="flex-1 border border-blue-200 text-slate-600 px-4 py-2 rounded-md font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessProfileInformation;